"use client"

import { useState, useMemo } from "react"
import Link from "next/link"
import { useTranslations } from "next-intl"
import { cn } from "@/shared/lib/utils"
import { ArticleList } from "@/features/blog/components/article-list"
import type { BlogPost } from "@/shared/types/blog"
import { blogArchivePath, type UiLocale } from "@/shared/lib/i18n/config"

const POSTS_PER_PAGE = 24

interface BlogPageContentProps {
  posts: BlogPost[]
  uiLocale: UiLocale
}

export function BlogPageContent({ posts, uiLocale }: BlogPageContentProps) {
  const t = useTranslations("blog.index")
  const [activeCategory, setActiveCategory] = useState("all")
  const [visibleCount, setVisibleCount] = useState(POSTS_PER_PAGE)

  const categories = useMemo(() => {
    // post.category is the human-readable display name set by the
    // sync-blog-posts edge function (PLEAA-524), e.g. "AI Companions",
    // "Reviews", "Guides", "Uncensored". Sort alphabetically so the filter
    // bar is stable across renders even when post-order changes.
    const uniqueCategories = Array.from(
      new Set(posts.map((p) => p.category).filter((c): c is string => Boolean(c)))
    ).sort((a, b) => a.localeCompare(b))
    return [
      { value: "all", label: t("allCategory") },
      ...uniqueCategories.map((cat) => ({ value: cat, label: cat })),
    ]
  }, [posts, t])

  const filteredPosts = useMemo(() => {
    if (activeCategory === "all") return posts
    return posts.filter((post) => post.category === activeCategory)
  }, [activeCategory, posts])

  const visiblePosts = filteredPosts.slice(0, visibleCount)
  const hasMore = visibleCount < filteredPosts.length

  function handleCategoryChange(category: string) {
    setActiveCategory(category)
    setVisibleCount(POSTS_PER_PAGE)
  }

  return (
    <div>
      {/* Hero */}
      <section className="bg-[#217df0] pb-12 pt-12 sm:pb-14 sm:pt-14 lg:pb-16 lg:pt-16">
        <div className="mx-auto max-w-[1080px] px-4 text-center sm:px-6 lg:px-8">
          <h1 className="font-[var(--font-blog-display)] text-[2.5rem] font-extrabold leading-none tracking-normal text-white sm:text-[3rem] lg:text-[4rem]">
            {t("heroTitle")}
          </h1>
          <p className="mx-auto mt-5 max-w-[620px] text-[1.0625rem] font-medium leading-[1.65] text-white/78">
            {t("heroSubtitle")}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="mx-auto max-w-[1080px] px-4 py-12 sm:px-6 lg:px-8 lg:py-14">
        {posts.length === 0 ? (
          <div className="py-20 text-center">
            <p className="text-lg text-[#9ca3af]">
              {t("emptyTitle")}
            </p>
          </div>
        ) : (
          <>
            {/* Category filters */}
            {categories.length > 1 && (
              <div className="blog-index-filter mb-11 flex flex-wrap items-center gap-2">
                {categories.map((cat) => (
                  <button
                    key={cat.value}
                    type="button"
                    onClick={() => handleCategoryChange(cat.value)}
                    className={cn(
                      "rounded-full px-4 py-1.5 text-[13px] font-semibold transition-all duration-150",
                      activeCategory === cat.value
                        ? "bg-[#1d7df2] text-white"
                        : "bg-transparent text-[#4b5563] hover:bg-[#f2f5f8] hover:text-[#111827]"
                    )}
                  >
                    {cat.label}
                  </button>
                ))}
              </div>
            )}

            <ArticleList posts={visiblePosts} uiLocale={uiLocale} />

            {hasMore && (
              <div className="mt-12 flex justify-center">
                <button
                  type="button"
                  onClick={() => setVisibleCount((prev) => prev + POSTS_PER_PAGE)}
                  className="rounded-full bg-[#1d7df2] px-8 py-3 text-sm font-medium text-white transition-colors hover:bg-[#1563c4]"
                >
                  {t("showMore", { count: filteredPosts.length - visibleCount })}
                </button>
              </div>
            )}

            {/* Link to full archive keeps /blog tight and AI-friendly,
                while ensuring every post is one click away via /blog/archive. */}
            {posts.length > POSTS_PER_PAGE && (
              <div className="mt-16 border-t border-gray-200 pt-10 text-center">
                <Link
                  href={blogArchivePath(uiLocale)}
                  className="inline-flex items-center gap-2 text-[14px] font-medium text-[#1d7df2] transition-colors hover:text-[#1563c4]"
                >
                  {t("archiveLink", { count: posts.length })}
                </Link>
              </div>
            )}
          </>
        )}
      </section>
    </div>
  )
}
